import { useSelector } from 'react-redux';
import React, { FC, useState, useEffect } from 'react';
import FilterSection from './FilterSection';
import { FilterState, FilterOption } from '../../types';
import { RootState } from '../../../../store/reducers';
import FW from '../../../../config/word.json';

export interface FilterPanelProps {
  isMobile: boolean;
  facilityOptions: FilterOption[];
  serviceOptions: FilterOption[];
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onApply: () => void;
  onClear: () => void;
}

const FilterPanel: FC<FilterPanelProps> = ({
  isMobile = false,
  facilityOptions,
  serviceOptions,
  filters,
  onFilterChange,
  onApply,
  onClear,
}) => {
  const [localFilters, setLocalFilters] = useState<FilterState>(filters);
  const { loading } = useSelector((state: RootState) => state.shopList);

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleToggleFacility = (id: string) => {
    const newFacilities = localFilters.facilities.includes(id)
      ? localFilters.facilities.filter((f) => f !== id)
      : [...localFilters.facilities, id];

    const newFilters = {
      ...localFilters,
      facilities: newFacilities,
    };

    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleToggleService = (id: string) => {
    const newServices = localFilters.services.includes(id)
      ? localFilters.services.filter((s) => s !== id)
      : [...localFilters.services, id];

    const newFilters = {
      ...localFilters,
      services: newServices,
    };

    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const handleApply = () => {
    onFilterChange(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      facilities: [],
      services: [],
    });
    onClear();
  };

  if (isMobile) {
    return (
      <details className="p-filter p-filter--sp-only">
        <summary className="p-filter__title">条件で絞り込む</summary>
        <div className="p-filter__inner">
          <FilterSection
            title="施設・設備"
            options={facilityOptions}
            selectedIds={localFilters.facilities}
            onToggle={handleToggleFacility}
          />
          <FilterSection
            title="サービス"
            options={serviceOptions}
            selectedIds={localFilters.services}
            onToggle={handleToggleService}
          />
        </div>
        <div className="p-filter__actions">
          <button type="button" className="c-button c-button--primary" onClick={handleApply}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                {FW.F_W_LOADING}
              </>
            ) : (
              <>絞り込む</>
            )}
          </button>
          <button type="button" className="p-filter__link-text c-link" onClick={handleClear}>
            クリア
          </button>
        </div>
      </details>
    );
  }

  return (
    <div className="p-filter p-filter--pc-only">
      <div className="p-filter__title">条件で絞り込む</div>
      <div className="p-filter__inner p-filter__inner--height c-scroll c-scroll--y">
        <FilterSection
          title="施設・設備"
          options={facilityOptions}
          selectedIds={localFilters.facilities}
          onToggle={handleToggleFacility}
        />
        <FilterSection
          title="サービス"
          options={serviceOptions}
          selectedIds={localFilters.services}
          onToggle={handleToggleService}
        />
      </div>
      <div className="p-filter__actions">
        <button type="button" className="c-button c-button--primary" onClick={handleApply}>
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              {FW.F_W_LOADING}
            </>
          ) : (
            <>絞り込む</>
          )}
        </button>
        <button type="button" className="p-filter__link-text c-link" onClick={handleClear}>
          クリア
        </button>
      </div>
    </div>
  );
};

export default FilterPanel;
